
import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

interface FAQSectionProps {
  nursingHomeName: string;
}

export function FAQSection({ nursingHomeName }: FAQSectionProps) {
  return (
    <section className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
      <h2 className="text-2xl font-semibold mb-4 text-grandpal-green-dark">Frequently Asked Questions</h2>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="item-1">
          <AccordionTrigger className="flex items-center">
            <div className="flex items-center">
              <HelpCircle className="h-5 w-5 text-grandpal-green-dark mr-2 flex-shrink-0" />
              <span>What services does {nursingHomeName} provide?</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {nursingHomeName} provides comprehensive nursing care services for elderly residents, including 24-hour nursing care, assistance with activities of daily living, medication management, and specialized care for residents with specific medical conditions. The facility also offers recreational activities, social events, and personalized care plans for each resident.
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="item-2">
          <AccordionTrigger className="flex items-center">
            <div className="flex items-center">
              <HelpCircle className="h-5 w-5 text-grandpal-green-dark mr-2 flex-shrink-0" />
              <span>How can I apply for admission to {nursingHomeName}?</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            To apply for admission, we recommend contacting the nursing home directly to schedule a visit and assessment. The admission process typically involves a review of medical history, assessment of care needs, and discussions about financial arrangements. Many residents use the Fair Deal Scheme to help cover costs.
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="item-3">
          <AccordionTrigger className="flex items-center">
            <div className="flex items-center">
              <HelpCircle className="h-5 w-5 text-grandpal-green-dark mr-2 flex-shrink-0" />
              <span>What are the visiting hours?</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            Standard visiting hours at {nursingHomeName} are typically from 10:00 AM to 8:00 PM daily. However, we understand that family circumstances may require flexibility, so arrangements can be made for visits outside these hours by contacting the staff in advance.
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="item-4">
          <AccordionTrigger className="flex items-center">
            <div className="flex items-center">
              <HelpCircle className="h-5 w-5 text-grandpal-green-dark mr-2 flex-shrink-0" />
              <span>Does {nursingHomeName} accept Fair Deal/Nursing Home Support Scheme?</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            Yes, {nursingHomeName} is an approved nursing home under the Nursing Home Support Scheme (Fair Deal). Residents can apply for financial support through this government scheme to help cover the cost of care. Our administration team can provide guidance on the application process.
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="item-5">
          <AccordionTrigger className="flex items-center">
            <div className="flex items-center">
              <HelpCircle className="h-5 w-5 text-grandpal-green-dark mr-2 flex-shrink-0" />
              <span>What activities are available for residents?</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {nursingHomeName} offers a variety of activities designed to enhance residents' quality of life and maintain cognitive and physical wellbeing. These include arts and crafts, music therapy, gentle exercise classes, gardening, book clubs, and regular social events. Activities are tailored to residents' interests and abilities.
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}
